import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Pencil, Save, X, NotebookPen, Flame, ThermometerSun, Zap, Snowflake, Clock, Wallet } from 'lucide-react';
import { logger } from '../lib/logger';

/**
 * ManualProfileFields — campi di profilo che l'agente aggiorna dalla scheda cliente.
 *
 * ★ 2026-07-03 (richiesta utente): Budget e Tempistiche ora si aggiornano con
 *   BOTTONI (grid) invece che con input free-text. Selezionando un valore, il
 *   backend aumenta automaticamente il lead-score e la temperature via
 *   `fire_budget_provided` / `fire_timeline_provided`.
 *
 * Campi disponibili:
 *   • profile_dominant     (archetipo TIME/SAFE/…)
 *   • profile_secondary
 *   • budget_bracket       (bottoni → id bracket, es. re_100_250)
 *   • timeline             (bottoni → id timeline)
 *   • budget_effettivo     (fallback free-text, campo "Altro")
 *   • tempo_disponibilita  (fallback free-text, campo "Altro")
 *   • tipo_nurturing       (free-text)
 *   • note_profilo         (free-text)
 *
 * Backend: POST /api/leads/{id}/manual-profile
 */

const ARCHETYPE_OPTIONS = [
  { code: 'TIME',   label: 'TIME · Opportunista strategico' },
  { code: 'SAFE',   label: 'SAFE · Protettivo patrimoniale' },
  { code: 'VISION', label: 'VISION · Visionario' },
  { code: 'LIFE',   label: 'LIFE · Stile di vita' },
  { code: 'CASH',   label: 'CASH · Cash-flow oriented' },
  { code: 'FAME',   label: 'FAME · Status / visibilità' },
  { code: 'HOME',   label: 'HOME · Casa / nido' },
  { code: 'DEEP',   label: 'DEEP · Analitico / profondo' },
  { code: 'GROW',   label: 'GROW · Crescita aggressiva' },
  { code: 'FREE',   label: 'FREE · Libertà / flessibilità' },
];

// Timeline options (mirror routes_public.VALID_TIMELINES + labels dal config)
// ★ 2026-07-03: pts = punti scoring (subito=20, 3m=15, 6m=10, oltre=5)
const TIMELINE_OPTIONS = [
  { id: 'subito', tone: 'blazing', pts: 20, get label() { return i18n.t('manualProfile.tl.now', 'Subito'); }, get sub() { return i18n.t('manualProfile.tl.nowSub', 'Entro 30 giorni'); } },
  { id: '3_months', tone: 'hot', pts: 15, get label() { return i18n.t('manualProfile.tl.3m', 'Tra 3 mesi'); }, get sub() { return i18n.t('manualProfile.tl.3mSub', 'Q1–Q2'); } },
  { id: '6_months', tone: 'warm', pts: 10, get label() { return i18n.t('manualProfile.tl.6m', 'Tra 6 mesi'); }, get sub() { return i18n.t('manualProfile.tl.6mSub', 'Pianificazione'); } },
  { id: 'over_6_months', tone: 'cold', pts: 5, get label() { return i18n.t('manualProfile.tl.over6m', 'Oltre 6 mesi'); }, get sub() { return i18n.t('manualProfile.tl.over6mSub', 'Ricerca info'); } },
];

// Colore/icona per il badge visuale del timeline
const TIMELINE_TONE = {
  blazing: { bg: 'bg-red-50 border-red-300 text-red-800 hover:bg-red-100', selected: 'bg-red-600 border-red-700 text-white shadow-md', icon: Zap },
  hot:     { bg: 'bg-orange-50 border-orange-300 text-orange-800 hover:bg-orange-100', selected: 'bg-orange-600 border-orange-700 text-white shadow-md', icon: Flame },
  warm:    { bg: 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100', selected: 'bg-amber-500 border-amber-600 text-white shadow-md', icon: ThermometerSun },
  cold:    { bg: 'bg-slate-50 border-slate-300 text-slate-700 hover:bg-slate-100', selected: 'bg-slate-600 border-slate-700 text-white shadow-md', icon: Snowflake },
};

export function ManualProfileFields({ lead, onUpdated }) {
  const { t } = useTranslation();
  const cf = lead?.custom_fields || {};
  const mp = cf.manual_profile || {};
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [budgetConfig, setBudgetConfig] = useState({ real_estate: [], equity_share: [] });

  const [form, setForm] = useState({
    profile_dominant: mp.profile_dominant || '',
    profile_secondary: mp.profile_secondary || '',
    budget_bracket: mp.budget_bracket || cf.qualifica_budget_bracket || cf.budget_bracket || '',
    budget_effettivo: mp.budget_effettivo || '',
    timeline: mp.timeline || cf.qualifica_timeline || '',
    tempo_disponibilita: mp.tempo_disponibilita || '',
    tipo_nurturing: mp.tipo_nurturing || '',
    note_profilo: mp.note_profilo || '',
  });

  // Carica opzioni budget dal config pubblico (già usato dalla QualificationModal)
  useEffect(() => {
    let cancelled = false;
    api.get('/public/qualifica/config').then((r) => {
      if (!cancelled) setBudgetConfig(r.data?.budget || { real_estate: [], equity_share: [] });
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Path del lead per scegliere i bracket giusti
  const leadPath = cf.qualifica_path || cf.interest_path || cf.path || 'real_estate';
  const budgetOptions = React.useMemo(() => {
    if (leadPath === 'equity_share') return budgetConfig.equity_share || [];
    if (leadPath === 'both') {
      // Mostra tutti — real_estate parte più basso
      const all = [...(budgetConfig.real_estate || []), ...(budgetConfig.equity_share || [])];
      const seen = new Set();
      return all.filter((b) => { if (seen.has(b.id)) return false; seen.add(b.id); return true; });
    }
    return budgetConfig.real_estate || [];
  }, [budgetConfig, leadPath]);

  const startEdit = () => {
    setForm({
      profile_dominant: mp.profile_dominant || '',
      profile_secondary: mp.profile_secondary || '',
      budget_bracket: mp.budget_bracket || cf.qualifica_budget_bracket || cf.budget_bracket || '',
      budget_effettivo: mp.budget_effettivo || '',
      timeline: mp.timeline || cf.qualifica_timeline || '',
      tempo_disponibilita: mp.tempo_disponibilita || '',
      tipo_nurturing: mp.tipo_nurturing || '',
      note_profilo: mp.note_profilo || '',
    });
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/leads/${lead.id}/manual-profile`, form);
      const delta = r?.data?._scoring_delta;
      if (delta && typeof delta.delta === 'number') {
        const arrow = delta.delta > 0 ? '↑' : (delta.delta < 0 ? '↓' : '=');
        const sign = delta.delta > 0 ? '+' : '';
        const tempChanged = delta.prev_temp !== delta.new_temp;
        toast.success(
          `${i18n.t('manualProfile.savedScore', 'Profilo salvato · Score')} ${arrow} ${sign}${delta.delta} (${delta.prev_score} → ${delta.new_score})`
          + (tempChanged
            ? ` · ${i18n.t('manualProfile.temperature', 'Temperatura')} ${(delta.prev_temp || '—').toUpperCase()} → ${(delta.new_temp || '—').toUpperCase()}`
            : ''),
          { duration: 5000 },
        );
      } else {
        toast.success(i18n.t('manualProfile.saved', 'Profilo manuale aggiornato'));
      }
      setEditing(false);
      onUpdated?.();
    } catch (err) {
      logger.error('manual-profile save error:', err);
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(' · ')
        : (detail || i18n.t('manualProfile.saveError', 'Errore salvataggio profilo'));
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const hasAnyValue = !!(mp.profile_dominant || mp.profile_secondary
    || mp.budget_bracket || mp.budget_effettivo
    || mp.timeline || mp.tempo_disponibilita
    || mp.tipo_nurturing || mp.note_profilo);

  // Etichetta leggibile per il bracket salvato
  const currentBracketLabel = React.useMemo(() => {
    const bid = mp.budget_bracket || cf.qualifica_budget_bracket || cf.budget_bracket;
    if (!bid) return null;
    const all = [...(budgetConfig.real_estate || []), ...(budgetConfig.equity_share || [])];
    return all.find((b) => b.id === bid)?.label || bid;
  }, [budgetConfig, mp.budget_bracket, cf.qualifica_budget_bracket, cf.budget_bracket]);

  const currentTimelineLabel = React.useMemo(() => {
    const tid = mp.timeline || cf.qualifica_timeline;
    if (!tid) return null;
    return TIMELINE_OPTIONS.find((t) => t.id === tid)?.label || tid;
  }, [mp.timeline, cf.qualifica_timeline]);

  return (
    <Card className="border-amber-200" data-testid="manual-profile-card">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <NotebookPen className="w-4 h-4 text-amber-700" />
            <span className="text-sm font-semibold text-amber-900">
              {t('manualProfile.title', 'Profilo manuale (compilazione agente)')}
            </span>
          </div>
          {!editing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={startEdit}
              data-testid="manual-profile-edit"
              className="h-7 text-xs"
            >
              <Pencil className="w-3 h-3 mr-1" />
              {hasAnyValue ? t('common.edit', 'Modifica') : t('manualProfile.fill', 'Compila')}
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={cancel}
                disabled={saving}
                className="h-7 text-xs"
                data-testid="manual-profile-cancel"
              >
                <X className="w-3 h-3 mr-1" /> {t('common.cancel', 'Annulla')}
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={saving}
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="manual-profile-save"
              >
                <Save className="w-3 h-3 mr-1" /> {saving ? t('common.saving', 'Salvataggio…') : t('common.save', 'Salva')}
              </Button>
            </div>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground -mt-1">
          {t('manualProfile.subtitle', 'Compila dopo la prima call. Budget/Tempistiche aggiornano automaticamente lo score e la temperatura del lead.')}
        </p>

        {/* ── VIEW MODE ──────────────────────────────────────────── */}
        {!editing && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <ReadOnlyField label="Profilo dominante" value={mp.profile_dominant} />
            <ReadOnlyField label="Profilo secondario" value={mp.profile_secondary} />
            <ReadOnlyField
              label="Budget"
              value={currentBracketLabel || mp.budget_effettivo}
              icon={Wallet}
            />
            <ReadOnlyField
              label="Tempistiche"
              value={currentTimelineLabel || mp.tempo_disponibilita}
              icon={Clock}
            />
            <div className="md:col-span-2">
              <ReadOnlyField label="Tipo di nurturing" value={mp.tipo_nurturing} multiline />
            </div>
            {mp.note_profilo && (
              <div className="md:col-span-2">
                <ReadOnlyField label="Note profilo" value={mp.note_profilo} multiline />
              </div>
            )}
            {mp.updated_by && (
              <div className="md:col-span-2 text-[10px] text-muted-foreground italic mt-1">
                Ultima modifica: {mp.updated_by}{mp.updated_at ? ` · ${new Date(mp.updated_at).toLocaleString('it-IT')}` : ''}
              </div>
            )}
          </div>
        )}

        {/* ── EDIT MODE ──────────────────────────────────────────── */}
        {editing && (
          <div className="space-y-4">
            {/* Row 1: Archetipi */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('manualProfile.dominant', 'Profilo dominante')}</Label>
                <Select
                  value={form.profile_dominant || 'none'}
                  onValueChange={(v) => setForm({ ...form, profile_dominant: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="manual-profile-dominant"><SelectValue placeholder={t('manualProfile.select', '— Seleziona —')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('manualProfile.none', '— Nessuno —')}</SelectItem>
                    {ARCHETYPE_OPTIONS.map((a) => (
                      <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('manualProfile.secondary', 'Profilo secondario')}</Label>
                <Select
                  value={form.profile_secondary || 'none'}
                  onValueChange={(v) => setForm({ ...form, profile_secondary: v === 'none' ? '' : v })}
                >
                  <SelectTrigger data-testid="manual-profile-secondary"><SelectValue placeholder={t('manualProfile.select', '— Seleziona —')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('manualProfile.none', '— Nessuno —')}</SelectItem>
                    {ARCHETYPE_OPTIONS.map((a) => (
                      <SelectItem key={a.code} value={a.code}>{a.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: BUDGET (bottoni) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5 text-emerald-700" />
                  {t('manualProfile.budgetBracket', 'Budget · fascia dichiarata')}
                </Label>
                {form.budget_bracket && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, budget_bracket: '' })}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                    data-testid="clear-budget-bracket"
                  >
                    {t('manualProfile.deselect', 'Deseleziona')}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {budgetOptions.map((b) => {
                  const selected = form.budget_bracket === b.id;
                  // ★ 2026-07-03: mostra hint punti in base al bracket max
                  const bMax = Number(b.max) || 0;
                  const budgetPts = bMax >= 1_000_000 ? 20
                    : bMax >= 500_000 ? 18
                    : bMax >= 250_000 ? 15
                    : bMax >= 100_000 ? 12
                    : bMax >= 50_000 ? 8 : 4;
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => setForm({ ...form, budget_bracket: b.id })}
                      className={`px-3 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all text-left ${
                        selected
                          ? 'bg-emerald-600 border-emerald-700 text-white shadow-md'
                          : 'bg-emerald-50 border-emerald-200 text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300'
                      }`}
                      data-testid={`manual-budget-bracket-${b.id}`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <span className="truncate">{b.label}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono shrink-0 ${
                          selected ? 'bg-white/25 text-white' : 'bg-emerald-200/60 text-emerald-800'
                        }`}>
                          +{budgetPts}
                        </span>
                      </div>
                    </button>
                  );
                })}
                {budgetOptions.length === 0 && (
                  <div className="col-span-full text-[11px] text-muted-foreground italic py-3 px-2 border border-dashed border-border rounded-md">
                    Path del lead non definito · seleziona un profilo Real Estate / Equity nel form iniziale.
                  </div>
                )}
              </div>
              <div className="pt-1">
                <Label className="text-[10px] text-muted-foreground">Oppure importo specifico dichiarato (facoltativo)</Label>
                <Input
                  placeholder="es. €80.000 oppure 50k-100k"
                  value={form.budget_effettivo}
                  onChange={(e) => setForm({ ...form, budget_effettivo: e.target.value })}
                  data-testid="manual-profile-budget"
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            {/* Row 3: TEMPISTICHE (bottoni) */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-orange-700" />
                  {t('manualProfile.timelineWhen', 'Tempistiche · quando è pronto')}
                </Label>
                {form.timeline && (
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, timeline: '' })}
                    className="text-[10px] text-muted-foreground hover:text-foreground underline"
                    data-testid="clear-timeline"
                  >
                    {t('manualProfile.deselect', 'Deseleziona')}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {TIMELINE_OPTIONS.map((t) => {
                  const selected = form.timeline === t.id;
                  const tone = TIMELINE_TONE[t.tone] || TIMELINE_TONE.cold;
                  const Icon = tone.icon;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setForm({ ...form, timeline: t.id })}
                      className={`px-3 py-2.5 rounded-lg border-2 text-xs font-semibold transition-all text-left ${
                        selected ? tone.selected : tone.bg
                      }`}
                      data-testid={`manual-timeline-${t.id}`}
                    >
                      <div className="flex items-center justify-between gap-1">
                        <div className="flex items-center gap-1.5">
                          <Icon className="w-3.5 h-3.5" />
                          <span>{t.label}</span>
                        </div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-mono ${
                          selected ? 'bg-white/25 text-white' : 'bg-white/60'
                        }`}>
                          +{t.pts}
                        </span>
                      </div>
                      <div className={`text-[10px] mt-0.5 ${selected ? 'text-white/80' : 'opacity-70'}`}>
                        {t.sub}
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="pt-1">
                <Label className="text-[10px] text-muted-foreground">{t('manualProfile.freeDesc', 'Oppure descrizione libera (facoltativo)')}</Label>
                <Input
                  placeholder={t('manualProfile.freeDescPh', 'es. dopo vacanze estive · Q4 2026 · quando vende casa')}
                  value={form.tempo_disponibilita}
                  onChange={(e) => setForm({ ...form, tempo_disponibilita: e.target.value })}
                  data-testid="manual-profile-time"
                  className="h-8 text-xs mt-1"
                />
              </div>
            </div>

            {/* Row 4: Nurturing / Note */}
            <div className="grid grid-cols-1 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">{t('manualProfile.nurturingType', 'Tipo di nurturing')}</Label>
                <Textarea
                  rows={2}
                  placeholder={t('manualProfile.nurturingPh', 'es. newsletter quindicinale + 1 call mensile · pushing diretto · solo follow-up email')}
                  value={form.tipo_nurturing}
                  onChange={(e) => setForm({ ...form, tipo_nurturing: e.target.value })}
                  data-testid="manual-profile-nurturing"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{t('manualProfile.profileNotes', 'Note profilo')}</Label>
                <Textarea
                  rows={2}
                  placeholder={t('manualProfile.profileNotesPh', 'Note libere aggiuntive sul profilo del cliente…')}
                  value={form.note_profilo}
                  onChange={(e) => setForm({ ...form, note_profilo: e.target.value })}
                  data-testid="manual-profile-notes"
                />
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReadOnlyField({ label, value, multiline = false, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-0.5 flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3" /> : null}
        {label}
      </div>
      <div
        className={`text-sm ${value ? 'text-foreground font-medium' : 'text-muted-foreground italic'} ${multiline ? 'whitespace-pre-line' : ''}`}
        data-testid={`manual-profile-readonly-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {value || '— non compilato —'}
      </div>
    </div>
  );
}
